import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
} from 'react-aria-components';
import { parseDate, getLocalTimeZone, today } from '@internationalized/date';
import { I18nProvider } from 'react-aria';
import dayjs from 'dayjs';
import "../general/DatePickerRater.css";

// Error handler remains the same
const handleResizeObserverError = (error) => {
  if (error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    return;
  }
  console.error('ResizeObserver error:', error);
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', handleResizeObserverError);
}

const DatePickerViolation = ({
  label = "Date",
  value,
  onChange,
  maxDate = null,
  disabled = false,
  error = false,
  helperText = "",
  fullWidth = true,
  startAdornment = null
}) => {
  // Convert dayjs to the format expected by react-aria DatePicker
  const convertDayjsToAriaDate = useCallback((dayjsDate) => {
    if (!dayjsDate || !dayjs.isDayjs(dayjsDate)) return null;
    try {
      const formatted = dayjsDate.format('YYYY-MM-DD');
      return parseDate(formatted);
    } catch {
      return null;
    }
  }, []);

  // Convert react-aria date to dayjs
  const convertAriaDateToDayjs = useCallback((ariaDate) => {
    if (!ariaDate) return null;
    try {
      const formatted = `${ariaDate.year}-${String(ariaDate.month).padStart(2, '0')}-${String(ariaDate.day).padStart(2, '0')}`;
      return dayjs(formatted);
    } catch {
      return null;
    }
  }, []);

  // Use a ref to store the last valid complete date
  const lastValidDateRef = useRef(convertDayjsToAriaDate(value));
  const [internalValue, setInternalValue] = useState(() => convertDayjsToAriaDate(value));
  const [mode, setMode] = useState('day');
  const [visibleYear, setVisibleYear] = useState(internalValue?.year || today(getLocalTimeZone()).year);
  const [visibleMonth, setVisibleMonth] = useState(internalValue?.month || today(getLocalTimeZone()).month);

  // Update internal state when value prop changes from outside
  useEffect(() => {
    const newAriaDate = convertDayjsToAriaDate(value);
    if (newAriaDate && newAriaDate.toString() !== lastValidDateRef.current?.toString()) {
      setInternalValue(newAriaDate);
      lastValidDateRef.current = newAriaDate;
      if (newAriaDate) {
        setVisibleYear(newAriaDate.year);
        setVisibleMonth(newAriaDate.month);
      }
    }
  }, [value, convertDayjsToAriaDate]);

  // Calculate max date for react-aria DatePicker
  const maxAriaDate = useMemo(() => {
    if (maxDate && dayjs.isDayjs(maxDate)) {
      return convertDayjsToAriaDate(maxDate);
    }
    return convertDayjsToAriaDate(dayjs());
  }, [maxDate, convertDayjsToAriaDate]);

  // Handle date changes with better validation
  const handleDateChange = useCallback((newDate) => {
    // Always update internal value to show what user is typing
    setInternalValue(newDate);

    if (!newDate) {
      onChange(null);
      lastValidDateRef.current = null;
      return;
    }

    // Check if we have a complete and valid date
    try {
      const year = newDate.year;
      const month = newDate.month;
      const day = newDate.day;

      // Only process if we have a complete year (4 digits) and valid month/day
      if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        // Validate the date is real (e.g., not Feb 31)
        const testDate = new Date(year, month - 1, day);
        if (testDate.getFullYear() === year &&
            testDate.getMonth() === month - 1 &&
            testDate.getDate() === day) {

          // Valid complete date - update parent
          const dayjsDate = convertAriaDateToDayjs(newDate);
          if (dayjsDate && dayjsDate.isValid()) {
            onChange(dayjsDate);
            lastValidDateRef.current = newDate;
          }
        }
      }
    } catch (error) {
      // Silently handle parsing errors during typing
      console.debug('Date parsing in progress:', error);
    }
  }, [onChange, convertAriaDateToDayjs]);

  // Handle date selection from calendar
  const handleDateSelect = useCallback((date) => {
    setInternalValue(date);
    lastValidDateRef.current = date;
    const dayjsDate = convertAriaDateToDayjs(date);
    if (dayjsDate) {
      onChange(dayjsDate);
    }
  }, [onChange, convertAriaDateToDayjs]);

  // Year grid rendering
  const renderYearGrid = useCallback(() => {
    const currentYear = new Date().getFullYear();
    const startYear = Math.max(1900, visibleYear - 12);
    const endYear = Math.min(currentYear + 10, startYear + 23);
    const years = [];

    for (let y = startYear; y <= endYear; y++) {
      years.push(
        <div
          key={y}
          className={`year-cell ${y === visibleYear ? 'selected' : ''}`}
          onClick={() => {
            setVisibleYear(y);
            setMode('day');
          }}
        >
          {y}
        </div>
      );
    }

    return <div className="year-grid">{years}</div>;
  }, [visibleYear]);

  // Month grid rendering
  const renderMonthGrid = useCallback(() => {
    const months = Array.from({ length: 12 }, (_, i) =>
      new Date(0, i).toLocaleString('en-US', { month: 'short' })
    );

    return (
      <div className="month-grid">
        {months.map((monthName, index) => (
          <div
            key={index}
            className={`month-cell ${index + 1 === visibleMonth ? 'selected' : ''}`}
            onClick={() => {
              setVisibleMonth(index + 1);
              setMode('day');
            }}
          >
            {monthName}
          </div>
        ))}
      </div>
    );
  }, [visibleMonth]);

  // Navigation handlers
  const handlePrevNavigation = useCallback(() => {
    if (mode === 'year') {
      setVisibleYear(y => Math.max(1900, y - 12));
    } else if (mode === 'day') {
      if (visibleMonth === 1) {
        if (visibleYear > 1900) {
          setVisibleMonth(12);
          setVisibleYear(y => y - 1);
        }
      } else {
        setVisibleMonth(m => m - 1);
      }
    } else if (mode === 'month') {
      setVisibleYear(y => Math.max(1900, y - 1));
    }
  }, [mode, visibleMonth, visibleYear]);

  const handleNextNavigation = useCallback(() => {
    const currentYear = new Date().getFullYear();
    if (mode === 'year') {
      setVisibleYear(y => Math.min(currentYear + 10, y + 12));
    } else if (mode === 'day') {
      if (visibleMonth === 12) {
        if (visibleYear < currentYear + 10) {
          setVisibleMonth(1);
          setVisibleYear(y => y + 1);
        }
      } else {
        setVisibleMonth(m => m + 1);
      }
    } else if (mode === 'month') {
      setVisibleYear(y => Math.min(currentYear + 10, y + 1));
    }
  }, [mode, visibleMonth, visibleYear]);

  // Calendar title
  const calendarTitle = useMemo(() => {
    if (mode === 'year') {
      const currentYear = new Date().getFullYear();
      const startYear = Math.max(1900, visibleYear - 12);
      const endYear = Math.min(currentYear + 10, visibleYear + 11);
      return `${startYear} - ${endYear}`;
    } else {
      return (
        <span>
          <span
            onClick={() => setMode('month')}
            style={{ cursor: 'pointer', marginRight: '4px', textDecoration: 'underline' }}
          >
            {new Date(visibleYear, visibleMonth - 1).toLocaleString('en-US', { month: 'long' })}
          </span>
          <span
            onClick={() => setMode('year')}
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            {visibleYear}
          </span>
        </span>
      );
    }
  }, [mode, visibleYear, visibleMonth]);

  // Focused calendar value
  const focusedValue = useMemo(() => {
    try {
      return parseDate(`${visibleYear}-${String(visibleMonth).padStart(2, '0')}-01`);
    } catch {
      return today(getLocalTimeZone());
    }
  }, [visibleYear, visibleMonth]);

  return (
    <I18nProvider locale="en-US">
      <div className="date-picker-container" style={{ width: fullWidth ? '100%' : 'auto' }}>
        <DatePicker
          label={label}
          value={internalValue}
          onChange={handleDateChange}
          maxValue={maxAriaDate}
          minValue={parseDate('1900-01-01')}
          className="react-aria-DatePicker"
          granularity="day"
          shouldForceLeadingZeros={true}
          isDisabled={disabled}
        >
          <Group className={`react-aria-Group ${error ? 'error' : ''}`}>
            {startAdornment && (
              <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
                {startAdornment}
              </div>
            )}

            <DateInput className="react-aria-DateInput">
              {(segment) =>
                segment.type === 'literal' ? (
                  <span key={segment.type} className="separator">/</span>
                ) : (
                  <DateSegment
                    key={segment.type}
                    segment={segment}
                    className="react-aria-DateSegment"
                  />
                )
              }
            </DateInput>

            <Button className="react-aria-Button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </Button>
          </Group>

          {helperText && (
            <div style={{
              color: error ? '#d32f2f' : '#666',
              fontSize: '12px',
              marginTop: '4px',
              minHeight: '16px'
            }}>
              {helperText}
            </div>
          )}

          <Popover className="react-aria-Popover">
            <Dialog className="react-aria-Dialog">
              <div className="calendar-header">
                <Button onPress={handlePrevNavigation}>◀</Button>
                <Heading className="calendar-title">
                  {calendarTitle}
                </Heading>
                <Button onPress={handleNextNavigation}>▶</Button>
              </div>

              {mode === 'year' && renderYearGrid()}
              {mode === 'month' && renderMonthGrid()}

              {mode === 'day' && (
                <Calendar
                  focusedValue={focusedValue}
                  value={internalValue}
                  onChange={handleDateSelect}
                  maxValue={maxAriaDate}
                  minValue={parseDate('1900-01-01')}
                >
                  <CalendarGrid className="react-aria-CalendarGrid">
                    {(date) => (
                      <CalendarCell
                        date={date}
                        className="react-aria-CalendarCell"
                      />
                    )}
                  </CalendarGrid>
                </Calendar>
              )}
            </Dialog>
          </Popover>
        </DatePicker>
      </div>
    </I18nProvider>
  );
};

export default DatePickerViolation;